import { useState } from 'react';
import { Paperclip, Trash2, Download, X } from 'lucide-react';
import { getFileUrl } from '../lib/api';

export const fileAuthUrl = (path) => `${getFileUrl(path)}?auth=${localStorage.getItem('access_token')}`;
export const isImageAttachment = (contentType) => (contentType || '').startsWith('image/');

export function AttachmentGallery({ attachments = [], canDelete = false, onDelete }) {
  const [preview, setPreview] = useState(null);
  const images = attachments.filter(a => isImageAttachment(a.content_type));
  const files = attachments.filter(a => !isImageAttachment(a.content_type));

  if (attachments.length === 0) return null;

  return (
    <div data-testid="attachment-gallery">
      {images.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-1">
          {images.map(a => (
            <div key={a.id} className="relative group rounded-md overflow-hidden border border-[#E0E0E0] bg-[#F5F5F5]">
              <button
                type="button"
                onClick={() => setPreview(a)}
                className="block w-full"
                title={a.filename}
                data-testid={`attachment-thumb-${a.id}`}
              >
                <img src={fileAuthUrl(a.storage_path)} alt={a.filename} className="w-full h-20 object-cover" loading="lazy" />
              </button>
              {canDelete && (
                <button
                  type="button"
                  onClick={() => onDelete(a.id)}
                  className="absolute top-1 right-1 p-1 rounded bg-white/90 text-[#999999] hover:text-[#DC2626] opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`delete-attachment-${a.id}`}
                >
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {files.length > 0 && (
        <div className="space-y-1 mt-2">
          {files.map(a => (
            <div key={a.id} className="flex items-center justify-between bg-[#F5F5F5] rounded px-2 py-1.5" data-testid={`attachment-${a.id}`}>
              <a
                href={fileAuthUrl(a.storage_path)}
                download={a.filename}
                className="flex items-center gap-1.5 text-sm text-[#111111] hover:text-[#FF6B35] truncate"
                data-testid={`download-attachment-${a.id}`}
              >
                <Paperclip size={13} />
                <span className="truncate">{a.filename}</span>
                <Download size={12} className="text-[#999999] flex-shrink-0" />
              </a>
              {canDelete && (
                <button
                  type="button"
                  onClick={() => onDelete(a.id)}
                  className="text-[#999999] hover:text-[#DC2626]"
                  data-testid={`delete-attachment-${a.id}`}
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {preview && (
        <div
          className="fixed inset-0 z-[70] bg-black/90 flex flex-col items-center justify-center p-4"
          onClick={() => setPreview(null)}
          data-testid="attachment-lightbox"
        >
          <img
            src={fileAuthUrl(preview.storage_path)}
            alt={preview.filename}
            className="max-h-[82vh] max-w-full object-contain rounded"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="flex items-center gap-3 mt-4" onClick={(e) => e.stopPropagation()}>
            <span className="text-white text-sm truncate max-w-[50vw]">{preview.filename}</span>
            <a
              href={fileAuthUrl(preview.storage_path)}
              download={preview.filename}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white text-[#111111] text-sm font-medium hover:bg-[#F0F0F0]"
              data-testid="lightbox-download-btn"
            >
              <Download size={14} /> Download
            </a>
            <button
              type="button"
              onClick={() => setPreview(null)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/20 text-white text-sm font-medium hover:bg-white/30"
              data-testid="lightbox-close-btn"
            >
              <X size={14} /> Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
